import React from 'react';
import { motion, useInView } from 'motion/react';
import { BentoCard, BentoGrid } from '@/components/ui/bento-grid';
import { useRef } from 'react';
import { 
  Monitor,
  Smartphone,
  Terminal,
  Layers,
  Database,
  GitBranch
} from 'lucide-react';
import { BlurText } from '@/components/ui/animated-blur-text';

const services = [
  {
    Icon: Monitor,
    icon: <Monitor className="w-6 h-6 text-foreground/70" />,
    name: "웹 개발",
    description: "고성능 웹 애플리케이션 개발",
    href: "#contact",
    cta: "프로젝트 시작하기",
    className: "lg:row-start-1 lg:row-end-3 lg:col-start-1 lg:col-end-2",
    tags: ["React", "Node.js", "Spring Boot"],
  },
  {
    Icon: Smartphone,
    icon: <Smartphone className="w-6 h-6 text-foreground/70" />,
    name: "모바일 앱 개발",
    description: "네이티브 & 크로스 플랫폼 앱 개발",
    href: "#contact",
    cta: "앱 개발 상담",
    className: "lg:col-start-2 lg:col-end-4 lg:row-start-1 lg:row-end-2",
    meta: "DAU 2,500+",
    tags: ["iOS", "Android", "Flutter"],
  },
  {
    Icon: Terminal,
    icon: <Terminal className="w-6 h-6 text-foreground/70" />,
    name: "퍼블리싱",
    description: "픽셀 퍼펙트 & 반응형 구현",
    href: "#contact",
    cta: "퍼블리싱 문의",
    className: "lg:col-start-2 lg:col-end-3 lg:row-start-2 lg:row-end-3",
    tags: ["HTML5", "CSS3", "JavaScript"],
  },
  {
    Icon: Database,
    icon: <Database className="w-6 h-6 text-foreground/70" />,
    name: "웹/앱 유지보수",
    description: "안정적인 서비스 운영 관리",
    href: "#contact",
    cta: "유지보수 계약",
    className: "lg:col-start-3 lg:col-end-4 lg:row-start-2 lg:row-end-3",
    tags: ["모니터링", "업데이트", "기술지원"],
    meta: "24/7",
  },
  {
    Icon: GitBranch,
    icon: <GitBranch className="w-6 h-6 text-foreground/70" />,
    name: "앱/웹 기획",
    description: "사용자 중심 서비스 설계",
    href: "#contact",
    cta: "기획 상담",
    className: "lg:col-start-1 lg:col-end-2 lg:row-start-3 lg:row-end-4",
    tags: ["UX", "프로토타입", "기획"],
  },
  {
    Icon: Layers,
    icon: <Layers className="w-6 h-6 text-foreground/70" />,
    name: "디자인",
    description: "모던한 UI/UX 디자인",
    href: "#contact",
    cta: "디자인 문의",
    className: "lg:col-start-2 lg:col-end-4 lg:row-start-3 lg:row-end-4",
    tags: ["UI/UX", "브랜딩", "프로토타입"],
  },
];

const ServicesBentoSection = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    }
  };

  const headerVariants = {
    hidden: { y: -20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <section ref={containerRef} className="py-20 px-4 bg-background">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          variants={headerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.div
            variants={{
              hidden: { scale: 0.9, opacity: 0 },
              visible: { 
                scale: 1, 
                opacity: 1,
                transition: { delay: 0.2, duration: 0.5 }
              }
            }}
          >
            <BlurText 
              text="전문 개발 서비스"
              className="text-4xl md:text-5xl text-foreground mb-4"
              duration={0.8}
            />
          </motion.div>
          <motion.p 
            className="text-xl text-foreground/70 max-w-3xl mx-auto"
            variants={{
              hidden: { y: 10, opacity: 0 },
              visible: { 
                y: 0, 
                opacity: 1,
                transition: { delay: 0.4, duration: 0.5 }
              }
            }}
          >
            4년간의 경험과 전문성으로 고객님의 비전을 현실로 만들어드립니다
          </motion.p>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <BentoGrid className="lg:grid-rows-3">
            {services.map((service, index) => (
              <motion.div
                key={service.name}
                variants={itemVariants}
                custom={index}
                whileHover={{ 
                  scale: 1.02,
                  transition: { duration: 0.2 }
                }}
              >
                <BentoCard {...service} />
              </motion.div>
            ))}
          </BentoGrid>
        </motion.div>

      </div>
    </section>
  );
};

export default ServicesBentoSection;